import { useState, useCallback } from 'react';
import { Col, Container, Row, Image } from 'react-bootstrap';
import LoaderScreen from './LoaderScreen';
import { BackendAPI, EVChargingStatus, SmartschankStatus } from '../services/BackendAPI';
import SuccessScreen from './SuccessScreen';
import ErrorScreen from './ErrorScreen';
import Layout from './Layout';
import { useStatusPolling } from '../hooks/useStatusPolling';
import DebugOverlay from './DebugOverlay';

export type PosSettingProps = {
    cashboxid: string;
    accesstoken: string;
}

// Move API instances outside component
const backendAPI = new BackendAPI();

const ChargingStateLabels: Record<number, string> = {
    0: 'Starting',
    1: 'Idle',
    2: 'Ready',
    3: 'Charging',
    4: 'Error',
    5: 'Unauthorized',
};

const PlugStateLabels: Record<number, string> = {
    0: 'Unplugged',
    1: 'Plugged (Station)',
    3: 'Locked (Station)',
    5: 'Plugged (EV)',
    7: 'Locked (EV)',
};

const getChargingStateLabel = (state: number): string =>
    ChargingStateLabels[state] ?? `Unknown (${state})`;

const getPlugStateLabel = (plugState: number): string =>
    PlugStateLabels[plugState] ?? `Unknown (${plugState})`;

type StationInfo = {
    message: string;
    variant: 'success' | 'warning' | 'danger' | 'info';
    available: boolean;
};

const getStationInfo = (status: EVChargingStatus | undefined, isLoading: boolean, hasError: boolean): StationInfo => {
    if (isLoading) return { message: 'Checking EV Charging status...', variant: 'info', available: false };
    if (hasError || !status) return { message: 'EV Charging unreachable', variant: 'danger', available: false };

    // Error states
    if (status.state === 4) return { message: 'EV Charging error', variant: 'danger', available: false };
    if (status.state === 5) return { message: 'EV Charging unauthorized', variant: 'danger', available: false };

    // Actively charging
    if (status.state === 3) return { message: 'Charging in progress', variant: 'info', available: false };

    // EV is connected
    if (status.plugState === 5)
        return { message: 'Vehicle connected - preparing to charge', variant: 'warning', available: false };
    if (status.plugState === 7)
        return { message: 'Vehicle connected and locked', variant: 'warning', available: false };

    // Available: Idle (1), Ready (2), or Starting (0) with no EV connected
    if (status.state === 0 || status.state === 1 || status.state === 2)
        return { message: 'EV Charging available', variant: 'success', available: true };

    return { message: `EV Charging not ready (${getChargingStateLabel(status.state)})`, variant: 'danger', available: false };
};

const Products = (settings: PosSettingProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>();
    const [qrCode, setQRCode] = useState<string>();
    const [journeyId, setJourneyId] = useState<string>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const [waitMessage, setWaitMessage] = useState<string>();
    const [productType, setProductType] = useState<'evcharging' | 'smartschank'>('evcharging');

    const [
        { data: chargingStatus, isLoading: isLoadingStatus, error: statusError },
    ] = useStatusPolling<EVChargingStatus>(
        () => backendAPI.getEvChargingStatus(),
        'charging station'
    );

    const [
        { data: smartschankStatus, isLoading: isLoadingSmartschankStatus, error: smartschankError },
    ] = useStatusPolling<SmartschankStatus>(
        () => backendAPI.getSmartschankStatus(),
        'smartschank'
    );

    const handleSmartschank = useCallback(async () => {
        setIsLoading(true);
        setWaitMessage("Creating cart...");
        try {
            let result = await backendAPI.executeSmartschank();
            setWaitMessage("Preauthorizing payment...");
            setIsLoading(false);
            setQRCode(result!.GiveAwayURL);
            setJourneyId(result!.ftJourneyID);
            setProductType('smartschank');
            setSuccess(true);
        } catch (error: any) {
            setIsLoading(false);
            setWaitMessage("");
            setError(true);
            setErrorMessage("Payment failed." + error.message);
        }
    }, []);

    const handleEvCharging = useCallback(async () => {
        setIsLoading(true);
        setWaitMessage("Creating cart...");
        try {
            let result = await backendAPI.executeEvCharging();
            setWaitMessage("Preauthorizing payment...");
            setIsLoading(false);
            setQRCode(result!.GiveAwayURL);
            setJourneyId(result!.ftJourneyID);
            setProductType('evcharging');
            setSuccess(true);
        } catch (error: any) {
            setIsLoading(false);
            setWaitMessage("");
            setError(true);
            setErrorMessage("Payment failed." + error.message);
        }
    }, []);


    const handleRetry = useCallback(() => {
        setError(false);
        handleSmartschank();
    }, [handleSmartschank]);

    const handleCancel = useCallback(() => {
        setError(false);
        setErrorMessage("");
    }, []);

    const handleDone = useCallback(() => {
        setSuccess(false);
        setQRCode("");
        setJourneyId("");
    }, []);

    if (error) {
        return (
            <ErrorScreen
                error={errorMessage}
                onRetry={handleRetry}
                onCancel={handleCancel}
            />
        );
    }

    if (success) {
        return (
            <SuccessScreen
                qrCode={qrCode!}
                journeyId={journeyId!}
                cashboxid={settings.cashboxid}
                accesstoken={settings.accesstoken}
                productType={productType}
                done={handleDone}
            />
        );
    }

    return (
        <Layout>
            {isLoading ? (
                <div className="w-100 h-100 d-flex align-items-center justify-content-center">
                    <LoaderScreen message={waitMessage ?? ""} noLayout={true} />
                </div>
            ) : (
                <Container fluid className="h-100 d-flex flex-column justify-content-center mt-4 product-items" style={{ maxWidth: '80%' }}>
                    <Row className="justify-content-center flex-grow-1">
                        <Col xs={12} className="d-flex flex-column justify-content-evenly h-100">
                            {/* EV Charging */}
                            {(() => {
                                const info = getStationInfo(chargingStatus, isLoadingStatus, !!statusError);
                                return (
                                    <div
                                        className={`device-card ${!info.available ? 'device-card--disabled' : ''}`}
                                        onClick={info.available ? () => handleEvCharging() : undefined}
                                        role="button"
                                        tabIndex={info.available ? 0 : -1}
                                    >
                                        <span className={`status-indicator ${isLoadingStatus ? 'status-indicator--loading' : info.available ? 'status-indicator--ok' : 'status-indicator--error'}`} />
                                        <Image
                                            src="evcharging.svg"
                                            className="device-card-icon"
                                            style={{ filter: info.available ? 'none' : 'grayscale(100%)' }}
                                        />
                                        <div className={`device-card-status device-card-status--${info.variant}`}>
                                            <span>{info.message}</span>
                                            {chargingStatus && info.available && (
                                                <span className="ms-2">| Plug: {getPlugStateLabel(chargingStatus.plugState)}</span>
                                            )}
                                            {chargingStatus && chargingStatus.state === 3 && (
                                                <span className="ms-2">| {chargingStatus.current}A | {chargingStatus.power}W | {chargingStatus.energy}Wh</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Smartschank */}
                            {(() => {
                                const ssAvailable = !smartschankError && !!smartschankStatus;
                                const ssVariant = smartschankError ? 'danger' : smartschankStatus ? 'success' : 'info';
                                const ssMessage = smartschankError ? 'Smartschank unreachable' : smartschankStatus ? 'Smartschank available' : 'Checking Smartschank status...';
                                return (
                                    <div
                                        className={`device-card ${!ssAvailable ? 'device-card--disabled' : ''}`}
                                        onClick={ssAvailable ? () => handleSmartschank() : undefined}
                                        role="button"
                                        tabIndex={ssAvailable ? 0 : -1}
                                    >
                                        <span className={`status-indicator ${isLoadingSmartschankStatus ? 'status-indicator--loading' : smartschankError ? 'status-indicator--error' : 'status-indicator--ok'}`} />
                                        <Image
                                            src="coffee-cup.svg"
                                            className="device-card-icon"
                                            style={{ filter: ssAvailable ? 'none' : 'grayscale(100%)' }}
                                        />
                                        <div className={`device-card-status device-card-status--${ssVariant}`}>
                                            <span>{ssMessage}</span>
                                        </div>
                                    </div>
                                );
                            })()}
                        </Col>
                    </Row>
                </Container>
            )}
            <DebugOverlay actions={[
                {
                    label: 'Show Success Screen',
                    onClick: () => {
                        setQRCode('https://example.com/debug-qr');
                        setJourneyId('00000000-0000-0000-0000-000000000000');
                        setSuccess(true);
                    }
                },
                {
                    label: 'Show Error Screen',
                    onClick: () => {
                        setErrorMessage('Debug error message');
                        setError(true);
                    }
                },
                {
                    label: 'Show Loading',
                    onClick: () => {
                        setWaitMessage('Debug loading...');
                        setIsLoading(true);
                    }
                },
            ]} />
        </Layout>
    );
};

export default Products;